import store from "@/store";
import { referralRewardsKnownEmpty } from "./referralRewards";

// Remaining uses per service, as kept in store/modules/login.js (hydrated at
// login, refreshed live by the `planifications_uses_update` socket message).
const USES_FIELD = {
  eureka: "eurekaUses",
  planification: "planificationsUses",
  materials: "materialsUses",
  tests: "testsUses",
  documents: "documentsUses",
};

// Balance for a service, or null when it is unknown / not applicable (a paid
// school never spends uses). ponytail: unknown stays null so a missing value
// never tells a user they ran out.
export const remainingUses = (service) => {
  if (store.state.login.isPaid) return null;
  // Absent localStorage key -> null, and Number(null) is 0, not NaN: check the
  // raw value first so "not logged in" never reads as "out of uses".
  const raw = store.state.login[USES_FIELD[service]];
  if (raw === null || raw === undefined || raw === "") return null;
  const uses = Number(raw);
  return Number.isFinite(uses) ? uses : null;
};

// Out of uses = can't pay for one action. Every service charges 1 use per
// action and the backends guard on `>= 1`, so a leftover fraction (eureka_uses
// is a FloatField) is unusable and must read as "no uses left", not as "0.5".
export const isOutOfUses = (uses) => uses !== null && uses < 1;

// Cuál es la ÚNICA salida que se destaca cuando el saldo llega a cero.
//
// Destacar las cuatro formas de conseguir usos a la vez no destacaba ninguna, y
// la invitación es la que reparte usos a los dos lados y trae gente nueva: por
// eso es la elegida. Pero eso vale mientras el referido tenga algo que dar. Los
// montos viven en RedpieConfig y hoy están todos en cero, así que en un
// servicio sin montos la invitación es justamente el único botón que NO entrega
// un uso — y se lo estaríamos ofreciendo a alguien que acaba de quedarse sin
// ninguno. Ahí el destaque pasa a comprar, que siempre cumple.
//
// `referralRewardsKnownEmpty` dice "vacío" sólo cuando preguntamos y la
// respuesta fue cero; desconocido cae en "invite" y se comporta como antes.
const outOfUsesNudge = (service) =>
  referralRewardsKnownEmpty(service) ? "buy" : "invite";

// Balance line for the chatbot welcome / main message ("" when unknown).
//
// La frase de "sin usos" nombra la salida que se destaca abajo, así que las dos
// deciden juntas: si el texto invita, el botón que pulsa es el de invitar.
export const usesLine = (service) => {
  if (store.state.login.isPaid) return "✨ Tu colegio tiene usos ilimitados.";
  const uses = remainingUses(service);
  if (uses === null) return "";
  if (isOutOfUses(uses)) {
    const cta =
      outOfUsesNudge(service) === "invite"
        ? "Invita a un colega y ganan usos los dos 👇"
        : "Consigue más con las opciones destacadas 👇";
    return `⚠️ <strong>No te quedan usos para este servicio.</strong> ${cta}`;
  }
  const plural = uses === 1 ? "" : "s";
  return `🎟️ Cuentas con <strong>${uses}</strong> uso${plural} disponible${plural} para este servicio.`;
};

// The option that actually spends a use — the primary action of each chatbot.
const SPENDS_USES = [
  "Crear una planificación",
  "Aplicar Eureka a un estudiante",
  "Ocupar aplicaciones disponibles",
  "Ocupar usos disponibles",
  "Trabajar en un documento",
  "Corregir un test",
];

// Exportada porque los menús de regreso la necesitan: una opción ya usada no
// se vuelve a ofrecer (el usuario ya la leyó), y la única excepción es la
// acción del servicio — sin ella el menú se quedaría sin la razón de existir
// apenas alguien crea su primera planificación o corrige su primer test.
export const spendsUses = (ans) => SPENDS_USES.includes(ans.label);

// ¿Es este el botón que `outOfUsesNudge` eligió destacar?
const isNudged = (ans, service) =>
  outOfUsesNudge(service) === "invite"
    ? ans.nextIndex === "invite"
    : (ans.redirect || "").includes("buy_product");

// With uses left, point at the action that spends them; with none left, at la
// única salida que `outOfUsesNudge` eligió — y sólo a esa. Destacar a la vez las
// cuatro formas de conseguir usos no destacaba ninguna: la invitación es la que
// reparte usos a los dos lados y además trae gente nueva, así que es la que
// pulsa mientras el servicio tenga algo que repartir. Cuando no lo tiene, el
// destaque pasa a comprar en lugar de quedarse sin destacar nada.
// Vale para todos los servicios: los cinco chatbots arman sus menús con esta
// función.
// Unknown balance nudges nothing.
export const usesCardClass = (ans, service) => {
  const uses = remainingUses(service);
  if (store.state.login.isPaid || (uses !== null && !isOutOfUses(uses))) {
    return spendsUses(ans) ? "highlighted-option" : undefined;
  }
  if (isOutOfUses(uses) && isNudged(ans, service)) {
    return "highlighted-option pulse-option";
  }
  return undefined;
};

// The same fact as `usesLine`, shaped for a toolbar instead of a chat bubble:
// no markup, no call to action, and `null` whenever there is nothing honest to
// say. Returns { label, tone } so the caller owns the styling.
//
// Why the explicit token check: on logout the store writes `testsUses = 0`
// (store/modules/login.js), so a visitor with no account reads as "out of
// uses" rather than "unknown". `usesLine` never had to care — it only renders
// inside chatbots that already require a session — but this chip also mounts
// in the logged-out demo, where the honest answer is to say nothing at all.
export const usesChip = (service) => {
  if (!store.state.login.token) return null;
  if (store.state.login.isPaid) {
    return { label: "Usos ilimitados", tone: "paid" };
  }
  const uses = remainingUses(service);
  if (uses === null) return null;
  if (isOutOfUses(uses)) return { label: "Sin usos", tone: "empty" };
  const plural = uses === 1 ? "" : "s";
  return { label: `${uses} uso${plural}`, tone: uses <= 2 ? "low" : "ok" };
};
